import React, { useRef, useState, useEffect } from 'react';
import { Package, BarChart, Share2, Zap, Sliders, Search } from 'lucide-react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import placeholderImage from '../../assets/RGBNN.jpg';
import boxGreyTextLogo from '../../assets/Box_Grey_Text copy-01.svg';
import qrCodeImage from '../../assets/adobe-express-qr-code.svg';
// Add imports for new logos
import windowsLogo from '../../assets/windows.png';
import linuxLogo from '../../assets/Linux-Logo.wine.svg';

const MechInterpA5Banner = () => {
  const bannerRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [progress, setProgress] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState({
    placeholder: null,
    windows: null
  });
  
  // A5 dimensions: 148 × 210 mm (landscape orientation: 210 x 148 mm)
  const bannerWidthMm = 210; // Landscape orientation
  const bannerHeightMm = 148;
  const bannerAspectRatio = bannerWidthMm / bannerHeightMm;
  
  // Preload only raster images on component mount
  useEffect(() => {
    const preloadImages = async () => {
      try {
        setProgress(10);
        
        // Helper function to load a raster image as a data URL
        const loadImage = (src) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = (err) => reject(err);
            img.src = src;
          });
        };
        
        // Only preload raster images, not SVGs
        const placeholderDataUrl = await loadImage(placeholderImage);
        
        // Store loaded images
        setPreloadedImages({
          placeholder: placeholderDataUrl,
          windows: null // SVGs don't need preloading
        });
        
        setImagesLoaded(true);
        setProgress(0);
      } catch (error) {
        console.error('Failed to preload images:', error);
        // Continue anyway, we'll use the original images
        setImagesLoaded(true);
        setProgress(0);
      }
    };
    
    preloadImages();
  }, []);
  
  // The features data
  const features = [
    {
      title: "3D Neural Network Visualization",
      icon: <Package size={24} className="text-indigo-500" strokeWidth={1.5} />
    },
    {
      title: "Activation-Based Analysis",
      icon: <BarChart size={24} className="text-purple-500" strokeWidth={1.5} />
    },
    {
      title: "Connection Visualization", 
      icon: <Share2 size={24} className="text-pink-500" strokeWidth={1.5} />
    },
    {
      title: "Network Optimization",
      icon: <Zap size={24} className="text-blue-500" strokeWidth={1.5} />
    },
    {
      title: "Neural Engineered Networks",
      icon: <Sliders size={24} className="text-purple-500" strokeWidth={1.5} />
    },
    {
      title: "Interactive Inspection",
      icon: <Search size={24} className="text-pink-500" strokeWidth={1.5} />
    }
  ];

  // Toggle preview mode (for full-size preview)
  const togglePreviewMode = () => {
    setIsPreviewMode(!isPreviewMode);
    // Scroll to top when entering preview mode
    if (!isPreviewMode) {
      window.scrollTo(0, 0);
    }
  };

  // Handle export to PNG using html-to-image
  const handleExportPNG = async () => {
    if (!bannerRef.current || isExporting) return;
    
    try {
      setIsExporting(true);
      setProgress(20);

      // If we're not in preview mode, enter it first
      if (!isPreviewMode) {
        setIsPreviewMode(true);
        // Wait for component to update before taking screenshot
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      setProgress(40);
      
      // Use toPng to capture the banner
      const dataUrl = await toPng(bannerRef.current, {
        quality: 1.0,
        pixelRatio: 3, // Higher scale for better quality
        cacheBust: true,
        backgroundColor: '#000000', // Ensure black background
        style: {
          margin: 0,
          padding: 0,
        },
      });
      
      setProgress(70);
      
      // Create download link
      const link = document.createElement('a');
      link.download = 'mechinterp-a5-banner.png';
      link.href = dataUrl;
      link.click();
      
      setProgress(100);
      
      // Reset
      setTimeout(() => {
        setProgress(0);
        setIsExporting(false);
      }, 1000);
    } catch (error) {
      console.error('PNG export error:', error);
      alert('Failed to export PNG: ' + error.message);
      setIsExporting(false);
      setProgress(0);
    }
  };

  // Handle export to PDF
  const handleExportPDF = async () => {
    if (!bannerRef.current || isExporting) return;
    
    try {
      setIsExporting(true);
      setProgress(20);
      
      // If we're not in preview mode, enter it first
      if (!isPreviewMode) {
        setIsPreviewMode(true);
        // Wait for component to update before taking screenshot
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      setProgress(40);
      
      // Use toPng to capture the banner
      const dataUrl = await toPng(bannerRef.current, {
        quality: 1.0,
        pixelRatio: 3, // Higher scale for better quality
        cacheBust: true,
        backgroundColor: '#000000', // Ensure black background
        style: {
          margin: 0,
          padding: 0,
        },
      });
      
      setProgress(60);
      
      // Create PDF with A5 dimensions (landscape)
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a5',
        compress: true
      });
      
      // Add image to PDF
      pdf.addImage(dataUrl, 'PNG', 0, 0, bannerWidthMm, bannerHeightMm);
      
      setProgress(80);
      
      // Save PDF
      pdf.save('mechinterp-a5-banner.pdf');
      
      setProgress(100);
      
      // Reset
      setTimeout(() => {
        setProgress(0);
        setIsExporting(false);
      }, 1000);
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export PDF: ' + error.message);
      setIsExporting(false);
      setProgress(0);
    }
  };
  
  // Reset preview mode when component unmounts
  useEffect(() => {
    return () => {
      setIsPreviewMode(false);
    };
  }, []);

  // If images are still loading, show a loading indicator
  if (!imagesLoaded) {
    return (
      <div className="w-full bg-gray-200 min-h-screen flex flex-col items-center justify-center">
        <div className="mb-4 text-xl font-medium">Preparing banner...</div>
        <div className="w-64 bg-gray-300 h-3 rounded-full overflow-hidden">
          <div className="bg-blue-600 h-full" style={{ width: `${progress || 30}%` }}></div>
        </div>
        <p className="mt-4 text-gray-600">Loading images for optimal export quality</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-gray-200 min-h-screen flex flex-col items-center py-8">
      {/* Controls - only show when not in preview mode */}
      {!isPreviewMode && (
        <div className="w-full max-w-4xl mb-8 px-4">
          <h1 className="text-2xl font-bold mb-4">MechInterp A5 Landscape Banner (210×148mm)</h1>
          <div className="flex flex-wrap gap-4 mb-4">
            <button 
              onClick={togglePreviewMode}
              className="px-6 py-3 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 transition-colors"
            >
              Preview Full Size
            </button>
            <button 
              onClick={handleExportPNG}
              disabled={isExporting}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              Export as PNG
            </button>
            <button 
              onClick={handleExportPDF}
              disabled={isExporting}
              className="px-6 py-3 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              Export as PDF
            </button>
          </div>
          <p className="mt-2 text-gray-600">
            A5 Banner size: {bannerWidthMm} × {bannerHeightMm}mm - Aspect ratio: {bannerAspectRatio.toFixed(3)}
          </p>
          
          {/* Progress bar for exporting */}
          {progress > 0 && (
            <div className="mt-2">
              <div className="w-full bg-gray-300 h-2 rounded-full overflow-hidden">
                <div 
                  className="bg-blue-600 h-full transition-all duration-300 ease-out" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {isExporting ? `Exporting: ${progress}% complete` : ''}
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Exit preview button - only show when in preview mode */}
      {isPreviewMode && !isExporting && (
        <div className="fixed top-4 right-4 z-50">
          <button 
            onClick={togglePreviewMode}
            className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors shadow-lg"
          >
            Exit Preview
          </button>
        </div>
      )}
      
      {/* The A5 banner itself */}
      <div 
        ref={bannerRef}
        className={`bg-black text-white ${!isPreviewMode ? 'border-2 border-blue-500' : ''}`}
        style={{ 
          width: isPreviewMode ? `${bannerWidthMm * 3}px` : '100%',
          maxWidth: isPreviewMode ? `${bannerWidthMm * 3}px` : '600px',
          aspectRatio: `${bannerAspectRatio}`,
          margin: '0 auto',
        }}
      >
        {/* Left side - Image background */}
        <div className="w-1/2 h-full relative float-left">
          {/* Main image and content area */}
          <div className="relative h-full">
            <div 
              className="absolute inset-0" 
              style={{
                backgroundImage: `url(${preloadedImages.placeholder || placeholderImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                opacity: 0.7
              }}
            ></div>
            
            {/* Logo - Using SVG directly, positioned at top-left */}
            <div className="absolute top-3 left-3 z-10">
              <img 
                src={boxGreyTextLogo}
                alt="Mechinterp Logo" 
                className="h-10 w-auto"
              />
            </div>
            
            {/* Title and Subtitle */}
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 z-10">
              <div className="bg-black bg-opacity-50 p-3 rounded-lg backdrop-blur-sm">
                <h1 className="text-3xl font-bold mb-1">Opening the</h1>
                <h1 className="text-3xl font-bold mb-6">Black Box</h1>
                <p className="text-sm text-gray-200">
                  The first commercial platform for<br/>
                  mechanistic interpretability -<br>
                  </br>Visualise, optimise and truly understand AI behavior.
                </p>
              </div>
            </div>
          </div>
          
          {/* Logos at bottom - MODIFIED: Both logos and text shifted down */}
          <div className="absolute bottom-2 left-0 right-0 z-10">
            <div className="flex flex-col items-center">
              <div className="flex items-center justify-center pl-6 mb-0">
                <img src={windowsLogo} alt="Windows Logo" className="h-8 w-auto mr-4" />
                <img src={linuxLogo} alt="Linux Logo" className="h-14 w-auto" />
              </div>
              <p className="text-xs text-white text-center -mt-1">Available for Windows and Linux</p>
            </div>
          </div>
        </div>
        
        {/* Right side - Features */}
        <div className="w-1/2 h-full bg-black flex flex-col float-left">
          {/* Title */}
          <div className="p-2 pb-1">
            <h2 className="text-2xl font-bold text-center">Key Features</h2>
          </div>
          
          {/* Feature grid */}
          <div className="grid grid-cols-2 gap-2 px-3 flex-grow">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-gray-900 rounded-lg p-2 flex flex-col items-center justify-center border border-white border-opacity-20 h-22"
                style={{ backgroundColor: '#111827' }}
              >
                <div className="mb-1 flex items-center justify-center">
                  {feature.icon}
                </div>
                <div className="text-xs text-center font-medium">
                  {feature.title}
                </div>
              </div>
            ))}
          </div>
          
          {/* Bottom section with QR code */}
          <div className="mt-auto p-3 flex flex-col items-center">
            <div className="bg-gray-900 rounded-lg p-2 w-full mb-2 text-center flex items-center justify-between border border-white border-opacity-20" 
                 style={{ backgroundColor: '#111827' }}>
              <div className="flex-1">
                <p className="text-xs text-white">Join the mailing list for updates:</p>
                <p className="text-xs text-blue-400 font-bold">mechinterp.ai</p>
              </div>
              <div className="bg-white p-1 rounded w-12 h-12 flex items-center justify-center">
                <img 
                  src={qrCodeImage}
                  alt="QR Code" 
                  className="w-10 h-10 object-contain"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">{/* Copyright removed */}</p>
          </div>
        </div>
        <div style={{ clear: 'both' }}></div>
      </div>
    </div>
  );
};

export default MechInterpA5Banner;